import React from 'react';
import { MenuProps, Row } from 'antd';
import { Formik, Form } from 'formik';
import COSelect from 'shared/components/COSelect';
import COBarChartProduct from 'shared/components/COBarChartProduct';
import MyLineChart from 'views/CompanyDashboard/LineChart';
import styles from './ProductDashboardCharts.module.scss';
import { ProductDashboardChartsViewProps } from 'enums/ProductDashboardChartsEnums';
import {
  ProductStagesEnums,
  ProductStagesValuesEnums,
} from 'enums/ProductStagesEnums';
import { COProductCardPropsEnums } from 'enums/COProductCardEnums';
import { SortKeys, DownloadFileName } from 'enums/GraphDropdownEnums';
import { ProductsEnums } from 'enums/ProductsEnums';
import { CategoryOptions } from 'views/Dashboard';
import { YoYEmissionsProductResponse } from 'models/MainDashboard/yoyModel.model';
import { Product } from 'models/Product/product.model';
import { BreakdownDrowdownTypes } from '.';

interface ProductGraphsProps {
  productId: number;
  selectedType: boolean;
  setSelectedType: (value: boolean) => void;
  selectedBusinessModelValue: string;
  setSelectedBusinessModelValue: (value: string) => void;
  productTotalCarbonForEachCycle: {
    label: string;
    value: string;
  }[];
  productCarbonFootprintOptions: {
    value: string;
  }[];
  carbonValueOfEachStage?: CategoryOptions[];
  emissionEachStageLoading: boolean;
  handleSortProductForLifeCycle: MenuProps['onClick'];
  downloadProductEmissionCSV: () => void;
  yoyProductData: YoYEmissionsProductResponse | undefined;
  fetchYoYProductEmissions: (params: {
    productId: number;
    exportType?: string;
  }) => Promise<void>;
  newProduct: Product | undefined;
  productBreakdownTotalCarbonForEachCycle: {
    label: string;
    value: string;
  }[];
  selectedStage: BreakdownDrowdownTypes;
  setSelectedStage: React.Dispatch<
    React.SetStateAction<BreakdownDrowdownTypes>
  >;
  eachProductAnnualOptions: {
    label: string;
    value: string;
  }[];
  handleProductAnnualOptions: (value: string) => void;
  breakdownData?: CategoryOptions[];
  handleSortBreakDownProductForLifeCycle: MenuProps['onClick'];
  downloadProductBreakdownEmissionCSV: () => void;
  productEmissionForLifeCycleSort: string;
  productBreakdownEmissionForLifeCycleSort: string;
  getCurrentStageName: () => string | undefined;
  handleSubmit: () => void;
  initialValues: Object;
  fetchEmissionEachStage: (
    id: number,
    businessModal: string,
    in_percentage?: boolean
  ) => void;
}

const ProductGraphs: React.FC<ProductGraphsProps> = ({
  productId,
  selectedType,
  setSelectedType,
  selectedBusinessModelValue,
  setSelectedBusinessModelValue,
  productTotalCarbonForEachCycle,
  productCarbonFootprintOptions,
  carbonValueOfEachStage,
  emissionEachStageLoading,
  handleSortProductForLifeCycle,
  downloadProductEmissionCSV,
  yoyProductData,
  fetchYoYProductEmissions,
  newProduct,
  productBreakdownTotalCarbonForEachCycle,
  selectedStage,
  setSelectedStage,
  eachProductAnnualOptions,
  handleProductAnnualOptions,
  breakdownData,
  handleSortBreakDownProductForLifeCycle,
  downloadProductBreakdownEmissionCSV,
  productEmissionForLifeCycleSort,
  productBreakdownEmissionForLifeCycleSort,
  getCurrentStageName,
  handleSubmit,
  initialValues,
  fetchEmissionEachStage,
}) => {
  return (
    <Row className={styles.chartContainer}>
      <div className={styles.chartContainerWrapper}>
        <div className={styles.chartLifeCycle}>
          <div className={styles.chartLifeCycleHeadingContainer}>
            <h3>{ProductDashboardChartsViewProps.CHART_ONE_HEADING}</h3>
          </div>
          <div className={styles.typeOfTotal}>
            <Formik
              initialValues={{
                type: productTotalCarbonForEachCycle[0].value,
              }}
              onSubmit={handleSubmit}>
              <Form>
                <COSelect
                  name="type"
                  options={productTotalCarbonForEachCycle}
                  placeholderstring="Type"
                  defaultValue={productTotalCarbonForEachCycle[0].value}
                  onChange={value => {
                    let desiredValue;
                    if (value === productTotalCarbonForEachCycle[0].value) {
                      setSelectedType(true);
                      desiredValue = true;
                    } else {
                      setSelectedType(false);
                      desiredValue = false;
                    }
                    fetchEmissionEachStage(
                      productId,
                      selectedBusinessModelValue,
                      desiredValue
                    );
                  }}
                  allowClear={false}
                />
              </Form>
            </Formik>
          </div>
          <div className={styles.chartLifeCycleFieldsContainer}>
            <Formik
              initialValues={{
                category: 'B2B',
              }}
              onSubmit={handleSubmit}>
              <Form>
                <div className={styles.fieldContainer}>
                  <COSelect
                    name="category"
                    options={productCarbonFootprintOptions}
                    placeholderstring="Category"
                    defaultValue={productCarbonFootprintOptions[0].value}
                    onChange={(values: string) => {
                      fetchEmissionEachStage(productId, values, selectedType);
                      if (values === ProductStagesEnums.B2C) {
                        setSelectedBusinessModelValue(
                          ProductStagesValuesEnums.B2C
                        );
                      }
                      if (values === ProductStagesEnums.B2B) {
                        setSelectedBusinessModelValue(
                          ProductStagesValuesEnums.B2B
                        );
                      }
                    }}
                    allowClear={false}
                  />
                </div>
              </Form>
            </Formik>
          </div>
        </div>
        <div className={styles.chartLifeCycleContainer}>
          <COBarChartProduct
            data={carbonValueOfEachStage}
            loading={emissionEachStageLoading}
            graphDropDownProps={{
              handleSort: handleSortProductForLifeCycle,
              downloadCsv: downloadProductEmissionCSV,
            }}
            isPercentage={selectedType}
            isBreakdown={false}
            isSort={
              productEmissionForLifeCycleSort === SortKeys.VALUE_HIGH_TO_LOW ||
              productEmissionForLifeCycleSort === SortKeys.VALUE_LOW_TO_HIGH
                ? true
                : false
            }
            isDownload={
              newProduct?.status === COProductCardPropsEnums.APPROVED
                ? true
                : false
            }
            selectedOption={productEmissionForLifeCycleSort}
            graphName={DownloadFileName.PRODUCT_LIFE_CYCLE_STAGES_EMISSION}
          />
        </div>
      </div>
      <div className={styles.yoyEmissionChart}>
        <div className={styles.chartBreakdownHeadingContainer}>
          <h3 className={styles.yoyText}>
            {ProductDashboardChartsViewProps.YOY_TEXT}
          </h3>
        </div>
        <div className={styles.cardContainer}>
          <div className={styles.breakdownChart}>
            <MyLineChart
              isProdDashboard
              AxisProp={ProductDashboardChartsViewProps.YEAR}
              unit={ProductDashboardChartsViewProps.KGCO2}
              data={yoyProductData?.yoyEmission}
              fetchYoYEmissions={fetchYoYProductEmissions}
              orgId={productId}
              height={410}
              hasDownload={newProduct?.status === ProductsEnums.APPROVED}
            />
          </div>
        </div>
      </div>
      <div className={styles.chartBreakdownContainer}>
        <div className={styles.chartBreakdownWrapper}>
          <div className={styles.chartBreakdownHeadingContainer}>
            <h3>{ProductDashboardChartsViewProps.CHART_TWO_HEADING}</h3>
          </div>
          <div className={styles.breakdownTypeOfTotalContainer}>
            <div className={styles.breakdownTypeOfTotal}>
              <Formik
                initialValues={{
                  type: productBreakdownTotalCarbonForEachCycle[0].value,
                }}
                onSubmit={handleSubmit}>
                <Form>
                  <COSelect
                    name="type"
                    options={productBreakdownTotalCarbonForEachCycle}
                    placeholderstring="Type"
                    defaultValue={
                      productBreakdownTotalCarbonForEachCycle[0].value
                    }
                    onChange={value => {
                      if (
                        value ===
                        productBreakdownTotalCarbonForEachCycle[0].value
                      ) {
                        setSelectedStage(
                          prev =>
                            prev && {
                              ...prev,
                              type: productBreakdownTotalCarbonForEachCycle[0]
                                .value,
                            }
                        );
                      } else if (
                        value ===
                        productBreakdownTotalCarbonForEachCycle[1].value
                      ) {
                        setSelectedStage(
                          prev =>
                            prev && {
                              ...prev,
                              type: productBreakdownTotalCarbonForEachCycle[1]
                                .value,
                            }
                        );
                      } else {
                        setSelectedStage(
                          prev =>
                            prev && {
                              ...prev,
                              type: productBreakdownTotalCarbonForEachCycle[2]
                                .value,
                              stage: selectedStage?.stage,
                            }
                        );
                      }
                    }}
                    allowClear={false}
                  />
                </Form>
              </Formik>
            </div>
            <div className={styles.chartBreakdownFieldsContainer}>
              <Formik initialValues={initialValues} onSubmit={handleSubmit}>
                <Form className={styles.chartBreakdownFields}>
                  <div className={styles.fieldContainerBig}>
                    <COSelect
                      name="category2"
                      options={eachProductAnnualOptions}
                      placeholderstring="Category"
                      defaultValue={eachProductAnnualOptions[0].value}
                      onChange={handleProductAnnualOptions}
                      allowClear={false}
                    />
                  </div>
                </Form>
              </Formik>
            </div>
          </div>
        </div>
        <div className={styles.breakdownChart}>
          <COBarChartProduct
            data={breakdownData}
            loading={false}
            graphDropDownProps={{
              handleSort: handleSortBreakDownProductForLifeCycle,
              downloadCsv: downloadProductBreakdownEmissionCSV,
            }}
            yAxisLabel={selectedStage?.type}
            isBreakdown
            isSort={
              productBreakdownEmissionForLifeCycleSort !== SortKeys.DEFAULT
                ? true
                : false
            }
            isDownload={
              newProduct?.status === COProductCardPropsEnums.APPROVED
                ? true
                : false
            }
            selectedOption={productBreakdownEmissionForLifeCycleSort}
            graphName={`product_${getCurrentStageName()}`}
          />
        </div>
      </div>
    </Row>
  );
};

export default ProductGraphs;
